import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Tooltip } from "@/components/Tooltip";

export type AvatarStackItem = {
    id: string;
    name: string;
    avatarUrl?: string;
    shape?: "circle" | "square";
    /**
     * The tooltip to override the default tooltip for the item.
     */
    tooltip?: string;
};

type AvatarStackProps = {
    items?: AvatarStackItem[];
    maxVisible?: number;
    disablePopover?: boolean;
    morePopoverContent?: ReactNode;
};

export const AvatarStack: FC<AvatarStackProps & PropsWithClassName> = ({
    items,
    maxVisible = 5,
    disablePopover = false,
    morePopoverContent,
    className,
}) => {
    const [popoverOpen, setPopoverOpen] = useState(false);

    const totalCount = items?.length ?? 0;
    const visibleCount = useMemo(() => Math.min(totalCount, maxVisible), [totalCount, maxVisible]);
    const visibleItems = useMemo(() => items?.slice(0, maxVisible), [items, maxVisible]);
    const remainingCount = useMemo(() => totalCount - maxVisible, [totalCount, maxVisible]);
    const showOverflow = useMemo(() => remainingCount > 0, [remainingCount]);

    const skeletonItems = useMemo(
        () =>
            [...Array<void>(visibleCount)].map((_, i) => {
                return (
                    <li
                        key={i}
                        className={cn(
                            "relative flex-shrink-0 transition-all ease-out",
                            i > 0 &&
                                (showOverflow
                                    ? "xl:max-[1380px]:group-focus-within/avatars:-ml-3 xl:max-[1380px]:group-hover/avatars:-ml-3 relative -ml-3 group-focus-within/avatars:-ml-2 group-hover/avatars:-ml-1 @[300px]:-ml-2.5"
                                    : "relative -ml-3 group-focus-within/avatars:ml-0.5 group-hover/avatars:-ml-1 @[300px]:-ml-2.5"),
                        )}
                        style={{
                            zIndex: visibleCount - i,
                        }}
                    >
                        <Avatar
                            size={24}
                            className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light transition-shadow group-focus-within/avatars:shadow-sm group-hover/avatars:shadow-sm"
                        >
                            <AvatarFallback>
                                <AvatarInitials name={""} size={24} />
                            </AvatarFallback>
                        </Avatar>
                    </li>
                );
            }),
        [visibleCount, showOverflow],
    );

    const renderItem = useCallback(
        (item: AvatarStackItem, index: number) => {
            const shapeClass = cn(item.shape === "square" ? "rounded-md" : "rounded-full");

            return (
                <li
                    key={item.id}
                    className={cn(
                        "relative flex-shrink-0 transition-all ease-out",
                        index > 0 &&
                            (showOverflow
                                ? "xl:max-[1380px]:group-focus-within/avatars:-ml-3 xl:max-[1380px]:group-hover/avatars:-ml-3 relative -ml-3 group-focus-within/avatars:-ml-2 group-hover/avatars:-ml-1 @[300px]:-ml-2.5"
                                : "relative -ml-3 group-focus-within/avatars:ml-0.5 group-hover/avatars:-ml-1 @[300px]:-ml-2.5"),
                    )}
                    style={{
                        zIndex: visibleItems!.length - index,
                    }}
                >
                    <Tooltip content={item.tooltip ?? item.name} usePortal>
                        <Avatar
                            size={24}
                            className={cn(
                                "size-6 flex-shrink-0 border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light transition-shadow group-focus-within/avatars:shadow-sm group-hover/avatars:shadow-sm",
                                shapeClass,
                            )}
                        >
                            {item.avatarUrl && <AvatarImage src={item.avatarUrl} alt={`${item.name}'s avatar`} />}
                            <AvatarFallback>
                                <AvatarInitials name={item.name} size={24} />
                            </AvatarFallback>
                        </Avatar>
                    </Tooltip>
                </li>
            );
        },
        [showOverflow, visibleItems],
    );

    return (
        <ul
            className={cn(
                "group/avatars flex max-w-[140px] items-center whitespace-nowrap rounded-lg p-0.5",
                className,
            )}
            tabIndex={0}
            aria-label="Shared with groups and used by users"
        >
            {!visibleItems ? skeletonItems : visibleItems.map((item, index) => renderItem(item, index))}
            {showOverflow &&
                (disablePopover || !morePopoverContent ? (
                    <li
                        className="relative -ml-2 flex size-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-surface-glass bg-surface-secondary text-[11px] font-medium text-content-secondary ring-1 ring-border-light @[300px]:-ml-0.5"
                        title={`${remainingCount} more`}
                    >
                        <span className="leading-none">+{remainingCount}</span>
                    </li>
                ) : (
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                            <li
                                className="relative -ml-2 flex size-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-surface-glass bg-surface-secondary text-xs font-medium text-content-secondary ring-1 ring-border-light transition-all ease-out focus:outline-none group-focus-within/avatars:ml-1 group-hover/avatars:ml-1 @[300px]:-ml-1.5"
                                tabIndex={0}
                                aria-haspopup="dialog"
                                aria-expanded={popoverOpen}
                            >
                                <span className="leading-none">+{remainingCount}</span>
                                <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded bg-surface-invert px-2 py-1 text-xs text-content-invert opacity-0 transition-opacity group-focus-within/avatars:opacity-100 group-hover/avatars:opacity-100">
                                    Show all
                                </span>
                            </li>
                        </PopoverTrigger>
                        <PopoverContent align="start" sideOffset={8} className="w-72 max-w-full p-0">
                            {morePopoverContent}
                        </PopoverContent>
                    </Popover>
                ))}
        </ul>
    );
};
